// PER-GUN FIT DATA (owner tool, 2026-07-10): every gun's first-person seat, ADS offset, muzzle point and
// third-person seat, keyed by weapon id and saved to public/data/weapon_fit.json through the same dev
// endpoint as the resizers (F4 -> Objects -> Guns edits these LIVE on the equipped weapon, then Save).
// Missing ids/fields fall back to the legacy class-wide constants, so an untuned gun behaves exactly as
// before this system existed.

export type V3 = [number, number, number];
export interface WeaponFitEntry {
  // FOUR VIEWS, each independently tunable + saved (owner 2026-07-12): 1P hip, 1P ADS, 3P rest, 3P aim.
  // The *Aim/*Rot pairs are DELTAS added on top of their view's rest fields (fp/rot, p3/p3r) when ads/aim
  // is fully engaged, eased in exactly like the arm/body already blend into their aim pose -- so an
  // untouched delta (undefined) is a no-op and every gun tuned before this existed behaves unchanged.
  fp?: V3;       // 1P HIP model offset in the hand (overrides WEAPON_GLB's yOffset/zOffset, adds x)
  rot?: V3;      // 1P HIP extra model rotation (radians) on top of the base "+X barrel -> -Z" yaw
  ads?: V3;      // 1P ADS position delta (added to the arm's auto-solved aim pose)
  adsRot?: V3;   // 1P ADS rotation delta (radians) on the arm, independent of the hip `rot`
  muzzle?: V3;   // FP muzzle-flash socket (right-arm space) -- tracers/bullets leave from here (view-independent)
  p3?: V3;       // 3P REST held-model offset (added to buildWeapon3p's default seat)
  p3r?: V3;      // 3P REST rotation (radians, x/y/z) -- 1P and 3P frames are totally different, so this is its own vector
  p3Aim?: V3;    // 3P AIM position -- ABSOLUTE seat while aiming (unset = uses p3, so rest tuning carries over)
  p3rAim?: V3;   // 3P AIM rotation -- ABSOLUTE (unset = uses p3r); the pose blends rest<->aim as the body raises
  scale?: number; // overall scale multiplier on top of the resizer/base scale (1 = untouched, view-independent)
  scl?: V3;      // per-axis scale multiplier: [width x, height y, length z] (all 1 = untouched, view-independent)
  p3rot?: number; // legacy extra 3P yaw (older saves; p3r supersedes it)
}

const EMPTY: WeaponFitEntry = {};
let map: Record<string, WeaponFitEntry> = {};
const subs: Array<() => void> = [];

function emit(): void { for (const s of subs) s(); }

// Self-primes at module load, like resizers.ts. A missing file just means "no overrides yet".
void (async () => {
  try {
    const res = await fetch('/data/weapon_fit.json');
    if (res.ok) { map = (await res.json()) as Record<string, WeaponFitEntry>; emit(); }
  } catch { /* fresh clone / offline: defaults */ }
})();

export function weaponFit(id: string | null | undefined): WeaponFitEntry {
  return (id && map[id]) || EMPTY;
}
export function weaponFitAll(): Record<string, WeaponFitEntry> { return map; }
export function setWeaponFit(id: string, patch: Partial<WeaponFitEntry>): void {
  map[id] = { ...map[id], ...patch };
  emit();
}
export function onWeaponFitChange(cb: () => void): void { subs.push(cb); }

// Persist through the dev server (same middleware as the F4 OBJECTS resizers). Prod ships the committed file.
export async function saveWeaponFit(): Promise<boolean> {
  try {
    const res = await fetch('/__save_resizer', { method: 'POST', body: JSON.stringify({ file: 'weapon_fit.json', data: map }) });
    return res.ok;
  } catch { return false; }
}
